import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";
import DiscoveredEndpoints from "./DiscoveredEndpoints";
import DiscoverySkeleton from "./DiscoverySkeleton";
import EndpointTestView from "./EndpointTestView";
import AuthConfigPanel from "./AuthConfigPanel";
import ErrorMapView from "./ErrorMapView";
import StillWorkingBanner from "./StillWorkingBanner";

export default function IntegrationBuilderRightPanel({
    phase, apiType, endpoints, revealedEndpoints,
    currentTestEndpoint, currentTestResult, currentFix,
    credentials, setCredentials, onStartTesting,
    onApproveEndpoint, onSkipEndpoint, autoApprove, onToggleAutoApprove,
    pendingParams, onSubmitParams, onRetestEndpoint,
    onFixWithAI, isFixing,
    errorMap, revealedErrors, auth, isTaskRunning, taskStartedAt, onSkipToPublish,
}) {
    const { t } = useTranslation();
    const [showCredentials, setShowCredentials] = useState(false);
    const isDiscovering = phase === "crawling" || phase === "discovering";
    const showStillWorkingBanner = isTaskRunning && phase !== "crawling" && phase !== "discovering";

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {showStillWorkingBanner && (
                <StillWorkingBanner endpointCount={endpoints.length} errorCount={errorMap.length} authType={auth?.type} startedAt={taskStartedAt} />
            )}

            {phase === "auth" && (
                <AuthConfigPanel authType={auth?.type || "bearer"} auth={auth} credentials={credentials} onChange={setCredentials} onTest={onStartTesting} onSkipToPublish={!isTaskRunning ? onSkipToPublish : null} isTaskRunning={isTaskRunning} />
            )}

            {isDiscovering && endpoints.length === 0 && (
                <DiscoverySkeleton phase={phase} />
            )}

            {(isDiscovering || phase === "auth") && endpoints.length > 0 && (
                <DiscoveredEndpoints endpoints={endpoints} revealedCount={revealedEndpoints} />
            )}

            {phase === "testing" && (
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <TestProgress
                            total={Math.min(endpoints.filter((e) => apiType === "graphql" ? e.method === "QUERY" : e.method === "GET").length, 20)}
                            currentIdx={currentTestEndpoint ? endpoints.filter((e) => apiType === "graphql" ? e.method === "QUERY" : e.method === "GET").indexOf(currentTestEndpoint) : -1}
                            label={apiType === "graphql" ? "queries" : "read endpoints"}
                        />
                        <button onClick={() => setShowCredentials((p) => !p)} className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors shrink-0">
                            <Icon name="Key" className="w-3.5 h-3.5" />
                            {showCredentials ? t("common.hide") : t("builder.credentials")}
                        </button>
                    </div>
                    {showCredentials && (
                        <CredentialsEditor auth={auth} credentials={credentials} onChange={setCredentials} onClose={() => setShowCredentials(false)} />
                    )}
                    <EndpointTestView
                        endpoint={currentTestEndpoint} result={currentTestResult} fix={currentFix}
                        onApprove={onApproveEndpoint} onSkip={onSkipEndpoint}
                        autoApprove={autoApprove} onToggleAutoApprove={onToggleAutoApprove}
                        pendingParams={pendingParams} onSubmitParams={onSubmitParams}
                        onRetest={onRetestEndpoint} onFixWithAI={onFixWithAI} isFixing={isFixing}
                    />
                </div>
            )}

            {phase === "generating_errors" && (
                <ErrorMapView errors={errorMap} revealedCount={revealedErrors} />
            )}
        </div>
    );
}

function TestProgress({ total, currentIdx, label }) {
    if (currentIdx < 0) return null;
    return (
        <div className="flex-1 flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${((currentIdx + 1) / total) * 100}%` }} />
            </div>
            <span className="text-xs text-gray-500 shrink-0">{currentIdx + 1} / {total} {label}</span>
        </div>
    );
}

function CredentialsEditor({ auth, credentials, onChange, onClose }) {
    const { t } = useTranslation();
    const fields = auth?.credentialFields?.length > 0
        ? auth.credentialFields.map((cf) => ({ key: cf.name, label: cf.label, type: cf.type || "password" }))
        : [{ key: "apiKey", label: "API Key", type: "password" }];
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3 animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-800">{t("builder.updateCredentials")}</span>
                <button onClick={onClose} className="text-amber-400 hover:text-amber-600"><Icon name="X" className="w-4 h-4" /></button>
            </div>
            {fields.map((f) => (
                <input key={f.key} type={f.type} value={credentials[f.key] || ""} onChange={(e) => onChange({ ...credentials, [f.key]: e.target.value })}
                    placeholder={f.label} className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            ))}
            <p className="text-xs text-amber-600">{t("builder.credentialsHint")}</p>
        </div>
    );
}
